import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  Switch,
  Link,
  Stack,
  Popover,
  Fade,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropDownUp from "@mui/icons-material/ArrowDropUp";
import PopupMap from "./popupMap";
import FullResultsDialog from "./fullResultsDialog";
import StyledTooltip from "../../utils/styledToolTip";
import { formatDecimal } from "../../utils/numberFormat";

export default function ResultsTable({ selectedProjects, singleProject }) {
  const [showImprovement, setShowImprovement] = useState(false);
  const [openPopupMap, setOpenPopupMap] = useState(false);
  const [openFullResults, setOpenFullResults] = useState(false);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [selectedGrids, setSelectedGrids] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);

  const getBadgeStyle = (value) => {
    if (value > 0) return { backgroundColor: "#d4edda", color: "#155724" }; // verde claro
    if (value < 0) return { backgroundColor: "#f8d7da", color: "#721c24" }; // vermelho claro
    return { backgroundColor: "#fff3cd", color: "#856404" }; // amarelo claro
  };

  // "Indicator: Action name" when the exact action is known (see projectSelection.handleToggle)
  const actionText = (a) => `${a?.indicatorName ?? ""}${a?.actionName ? `: ${a.actionName}` : ""}`;

  // Mapeamento entre nome do indicador e chave no projectInfo
  const keyMapNeighbourhoodScore = {
    "Mobility Combined Score": "WalkabilityAndCyclabilityCombined",
    "Liveability Combined Score": "LivabilityCombined",
  };

  const hasAnyNegativeNeighbourhoodScoreValue = () => {
    for (const project of selectedProjects) {
      const info = project.projectInfo || {};
      // Para cada indicatorName, usamos o baseKey mapeado
      for (const indicatorName of Object.keys(keyMapNeighbourhoodScore)) {
        const baseKey = keyMapNeighbourhoodScore[indicatorName];

        // encontra a chave postProject* que contenha o baseKey
        const postKey = Object.keys(info).find(
          (k) => k.startsWith("postProject") && k.includes(baseKey)
        );

        const value = postKey ? info[postKey] : null;

        if (typeof value === "number" && value < 0) {
          return true; // curto-circuito: já sabemos que há negativo
        }
      }
    }
    return false;
  }

  console.log("hasAnyNegativeNeighbourhoodScoreValue=", hasAnyNegativeNeighbourhoodScoreValue());

  
  const hasAnyNegativeHealthScoreValue = () => {
    for (const project of selectedProjects) {
      const info = project?.projectInfo ?? {};

      // 1) Total de população (sempre mostrado; é a soma de populations)
      const selected = Array.isArray(info.selectedGrids) ? info.selectedGrids : [];
      const populationTotal = selected.reduce((acc, item) => {
        const pop = typeof item?.population === "number" ? item.population : 0;
        return acc + pop;
      }, 0);
      if (populationTotal < 0) return true;

      // 2) Média de minutos/semana (só mostra se physicalActivityAsPercentOfTheTarget != null)
      if (info.physicalActivityAsPercentOfTheTarget != null) {
        const avgMinutes = info.averageMinutesOfActivityPerUserPerWeek;
        if (typeof avgMinutes === "number" && avgMinutes < 0) return true;
      }

      // 3) Esperança média de vida (só mostra se lifeExpectancyAtBirth != null)
      if (info.lifeExpectancyAtBirth != null) {
        const lifeExp = info.lifeExpectancyAtBirth;
        if (typeof lifeExp === "number" && lifeExp < 0) return true;
      }
    }
    return false;
  }

  console.log("hasAnyNegativeHealthScoreValue=", hasAnyNegativeHealthScoreValue());

  const roundToTwo = (value) => {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  const formatWithSpaceThousands = (value) => {
    if (isNaN(value)) return "-";
    return formatDecimal(value);
  }

  //Popover stuff//
  const handlePopoverOpen = (event, project) => {
    setAnchorEl(event.currentTarget);
    setCurrentProject(project);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setCurrentProject(null);
  };

  const open = Boolean(anchorEl);

  const handleOpenFullResults = () => {
    //setProjects(selectedProjects); 
    setOpenFullResults(true);
  };
  const handleCloseFullResults = () => setOpenFullResults(false);

  const handleOpenPopupMap = (projectInfo) => {
    setSelectedCityId(projectInfo.cityId);
    setSelectedGrids(projectInfo.selectedGrids)
    setOpenPopupMap(true);
  };

  const handleClosePopupMap = () => {
    setOpenPopupMap(false);
    setSelectedCityId(null);
    setSelectedGrids([]);
  };

  // Indicadores para métricas
  const metrics = [
    {
      category: "Neighbourhood Score",
      items: [
        "Healthy Neighbourhood Index <span>(total)</span>",
        "Mobility",
        "Liveability",
      ],
    },
    {
      category: "Health",
      items: [
        "Number of people more active",
        "Increase in physical activity of community <span>(total minutes per week)</span>",
        "Life years gained <span>(years)</span>",
      ],
    },
    {
      category: "Environment",
      items: [
        "Reduction in car trips <span>(trips/year)</span>",
        "Reduced CO₂ <span>(tonnes/year)</span>",
        "Reduced PM2.5 <span>(kg/year)</span>",
      ],
    },
  ];

  return (
    <Box sx={{ mt: 0 }}>

      {/* Toggle Switch */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h5">
          RESULTS
        </Typography>        
        <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
          <Switch
            checked={showImprovement}
            onChange={(e) => setShowImprovement(e.target.checked)}
          />
          <Typography variant="body2" sx={{ ml: 1 }}>
            Show improvement percentage against current scores
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}
        {...(!singleProject && {
          sx: {justifyContent: "flex-start", minWidth: 774, overflowX: "auto"}
        })}
        {...(singleProject && {
          sx: { minWidth: 490, justifyContent: "space-between" }
        })}  
          >
        {/* LEFT COLUMN - Labels */}
        <Grid item
          {...(!singleProject && {
            sx: {
              textAlign: "left",
              width: 320,  //402
              minWidth: 320
            },
          })}
          {...(singleProject && {
            sx: {           
              width: '100%',
              maxWidth: 320,
              minWidth: 0,
              flexShrink: 1,
              mx: 'auto',
            }
          })}
        >

          {!singleProject && (
            <Stack spacing={1} sx={{ mb: 2, mt: 8 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Location
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Actions
              </Typography>
            </Stack>
          )}

          {metrics.map((section) => (
            <Box key={section.category} sx={{ mb: 2 }}>
              <Stack spacing={1}>
                <Typography className="subtitle_default" sx={{ mb: 1, mt: 1 }}>
                  {section.category} 

                  {((section?.category === "Neighbourhood Score" && hasAnyNegativeNeighbourhoodScoreValue()) || 
                    (section?.category === "Health" && hasAnyNegativeHealthScoreValue())) && 
                    (<StyledTooltip title="There is a negative score for this indicator" arrow>
                      <span style={{ backgroundPosition: "right", width: 24, backgroundRepeat: "no-repeat"}} className="icon"></span></StyledTooltip>)
                  }

                </Typography>
                {section.items.map((item) => (
                  <Typography
                    key={item}
                    variant="body2"
                    className="resultsTotal"
                    sx={{ mb: '4px !important' }}
                  >
                    <span dangerouslySetInnerHTML={{ __html: item }} />
                  </Typography>
                ))}
              </Stack>
            </Box>
          ))}
        </Grid>

        {/* RIGHT COLUMNS - Projects */}
        <Box sx={{ overflowX: "auto", whiteSpace: "nowrap", display: "flex", gap: 2, px: 1, py:2, width: "calc(100% - 320px)", justifyContent: singleProject ? "flex-end" : "space-between" }}>
          <Grid item {...(!singleProject && { sx: { justifyContent: singleProject ? "flex-end" : "space-between", display: "contents" }})}>
            <Grid
              container
              spacing={2}
              wrap="nowrap"
              sx={{ justifyContent: "flex-start" }}
            >
              {selectedProjects.map((project) => (
                <Grid
                  item
                  /*xs={3}
                  sm={3}
                  md={3}*/
                  key={project.projectNumber}
                  sx={{ 
                    flex: "1 1 250px",  // cresce, encolhe, base mínima
                    maxWidth: "300px",  // para não ficarem gigantes em ecrãs enormes
                    minWidth: "180px",  // mínimo confortável
                    minHeight: 460, '& .MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded': {maxHeight: 'none'} }}
                >
                  <Paper sx={{ p: 2, textAlign: "center", borderRadius: "8px", maxHeight: "none" }}>

                    {!singleProject && (
                    <>
                      {/* Header */}
                      <Box
                        color="primary-color"
                        sx={{
                          backgroundColor: "#23bced",
                          color: "#fff",
                          p: 1,
                          borderRadius: "4px",
                          mb: 1,
                          mt: 0,
                          minHeight: 40
                        }}
                      >
                        <Typography
                          variant="contained"
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block",
                          }}
                        >
                          {project.projectName}
                        </Typography>
                      </Box>

                      {/* Location */}
                      {project?.projectInfo.cityName ? (
                        <Link
                          href="#"
                          onClick={() =>
                            handleOpenPopupMap(project?.projectInfo)
                          }
                          sx={{
                            display: "block",
                            mt: 1,
                            color: "#23bced",
                            fontSize: "14px",
                          }}
                        >
                          {project?.projectInfo.cityName ?? "-"}
                        </Link>
                      ) : (
                        <Typography component="span">
                          -
                        </Typography>
                      )}

                      <PopupMap
                        open={openPopupMap}
                        onClose={handleClosePopupMap}
                        cityId={selectedCityId}
                        selectedGrids={selectedGrids}
                      />

                      <Popover
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handlePopoverClose}
                        transitionComponent={Fade} // Fade effect
                        transitionDuration={300} // duração do fade
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        disableRestoreFocus
                        sx={{ pointerEvents: "none" }}
                        slotProps={{
                          paper: {
                            sx: {
                              minWidth: 20, // evita encolher
                              maxWidth: 360, // long action names wrap instead of stretching the popover
                              overflowWrap: "anywhere",
                              boxShadow: "0px 2px 8px rgba(0,0,0,0.25)",
                              borderRadius: 2,
                              padding: 0,
                            },
                          },
                        }}>
                        {currentProject && (
                          <Box sx={{ p: 2, minWidth: 150 }}>
                            <Stack>                                                                         
                              {Array.isArray(currentProject?.projectInfo?.actions) && currentProject.projectInfo.actions.length > 0 ? (                                                                                  
                                currentProject.projectInfo.actions.map((a, idx) => {                                
                                  return (
                                    <Typography key={idx} variant="body2" sx={{ mt: 0.5}}>
                                      {actionText(a)}{' '}
                                      <Typography component="span" variant="body2" sx={{ color: '#9e9e9e'}} >
                                        ({a.level})
                                      </Typography>
                                    </Typography>
                                  )
                                })
                              ) : (
                                <Typography variant="body2">
                                  No actions defined
                                </Typography>
                              )}                     
                            </Stack>
                          </Box>
                        )}
                      </Popover>

                      {/* Actions */}
                      <Box sx={{ mt: 0 }}>
                        { (project?.projectInfo.actions && project?.projectInfo.actions.length === 1) ? (                                            
                          // One line only: the label column on the left assumes this row is a single line, so a
                          // long action name is ellipsized (full text on hover) while the level stays visible.
                          <StyledTooltip arrow title={`${actionText(project.projectInfo.actions[0])} (${project.projectInfo.actions[0].level})`}>
                            <Typography variant="body2" sx={{
                                display: "flex",
                                justifyContent: "center",
                                mt: 0.75, fontSize: "14px", minWidth: 0}}>
                              <Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                                {actionText(project.projectInfo.actions[0])}
                              </Box>
                              <Typography component="span" variant="body2" sx={{ color: 'text.secondary', flexShrink: 0, ml: 0.5 }}>
                                ({project?.projectInfo.actions[0].level})
                              </Typography>
                            </Typography>
                          </StyledTooltip>
                        ) : project?.projectInfo.actions?.length > 1 ? (
                          <Link
                            href="#"
                            onMouseEnter={(e) => handlePopoverOpen(e, project)}
                            onMouseLeave={handlePopoverClose}
                            sx={{
                              display: "block",
                              mt: 0.75,
                              color: "#23bced",
                              fontSize: "14px",
                            }}
                          >                  
                            Multiple Actions
                          </Link>
                        ) : (
                          <Typography component="span">
                            -
                          </Typography>
                        )}
                      </Box>
                      </>                
                    )}

                    {/* metrics Neighbourhood Score*/}
                    <Box sx={{ textAlign: "center", ...(singleProject ? {mt: 2} : { mt: 6 }), }}>
                      <Stack spacing={1}>
                        {(() => {
                          let total = 0;
                          let totalDiff = 0;
                          let totalBase = 0; // total base

                          const rows = Object.keys(keyMapNeighbourhoodScore).map(
                            (indicatorName) => {
                              const baseKey = keyMapNeighbourhoodScore[indicatorName];

                              const postKey = Object.keys(
                                project.projectInfo || {}
                              ).find(
                                (k) =>
                                  k.startsWith("postProject") &&
                                  k.includes(baseKey)
                              );

                              // Valor base
                              const baseKeyFound = Object.keys(
                                project.projectInfo || {}
                              ).find(
                                (k) =>
                                  k.startsWith("preProject") &&
                                  k.includes(baseKey)
                              );
                              const baseValue = baseKeyFound
                                ? project.projectInfo[baseKeyFound]
                                : null;
                              if (baseValue !== null) totalBase += baseValue;

                              const value = postKey
                                ? project.projectInfo[postKey]
                                : null;
                              if (value !== null) total += value;

                              // Difference percentage key
                              const diffKey = Object.keys(
                                project.projectInfo || {}
                              ).find(
                                (k) =>
                                  k.startsWith("difference") &&
                                  k.includes(baseKey)
                              );
                              const diffValue = diffKey
                                ? project.projectInfo[diffKey]
                                : null;

                              // per-row value; the shared totalDiff leaked the previous row's % into rows without a diff
                              const rowDiff =
                                diffValue !== null && baseValue !== null && baseValue !== 0
                                  ? (diffValue / baseValue) * 100
                                  : null;

                              return (
                                <Box
                                  key={indicatorName}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: 1, mt: "4px !important"
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    key={indicatorName}
                                    sx={{ width: "60px", py: 0.5 }}
                                  >
                                    {value !== null ? value.toFixed(2) : "-"}
                                  </Typography>

                                  {/* Percentagem com badge colorido */}
                                  {/* 0% is shown too (yellow badge), so the toggle is visible on no-change projects */}
                                  {showImprovement &&
                                    rowDiff !== null && (
                                      <Box
                                        className="badge"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          px: 1,
                                          py: 0.5,
                                          pr: 0.5,
                                          borderRadius: 1,
                                          fontWeight: "bold",
                                          ...getBadgeStyle(rowDiff),
                                        }}
                                      >
                                        {`${rowDiff.toFixed(2)}%`}
                                        {rowDiff > 0 && (
                                          <ArrowDropDownUp fontSize="small" />
                                        )}
                                        {rowDiff < 0 && (
                                          <ArrowDropDownIcon fontSize="small" />
                                        )}
                                      </Box>
                                    )}
                                </Box>
                              );
                            }
                          );

                          // ✅ Cálculo correto da percentagem total
                          totalDiff =
                            totalBase > 0
                              ? ((total - totalBase) / totalBase) * 100
                              : 0;

                          return (
                            <>
                              {/* Soma total com badge */}
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: "bold",
                                    width: "60px",
                                    py: 0.5,
                                  }}
                                >
                                  {total.toFixed(2)}
                                </Typography>

                                {showImprovement && totalBase > 0 && (
                                  <Box
                                    className="badge"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      px: 1,
                                      py: 0.5,
                                      pr: 0.5,
                                      borderRadius: 1,
                                      fontWeight: "bold",
                                      ...getBadgeStyle(totalDiff),
                                    }}
                                  >
                                    {`${totalDiff.toFixed(2)}%`}
                                    {totalDiff > 0 && (
                                      <ArrowDropDownUp fontSize="small" />
                                    )}
                                    {totalDiff < 0 && (
                                      <ArrowDropDownIcon fontSize="small" />
                                    )}
                                  </Box>
                                )}
                              </Box>
                              {rows}
                            </>
                          );
                        })()}
                      </Stack>

                    </Box>
                      
                    {/* metrics Health*/}
                    <Box sx={{ textAlign: "center", mt: 5.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ width: "60px", py: 1 }}
                        >                         
                          {
                            project.projectInfo.numberOfPeopleMoreActive != null 
                            ? formatWithSpaceThousands(project.projectInfo.numberOfPeopleMoreActive)
                            : "--"
                          }

                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ width: "60px", py: 0.5 }}
                        >
                          {
                          /* Fix wrong mapping
                            project.projectInfo.physicalActivityAsPercentOfTheTarget != null
                            ? roundToTwo(project.projectInfo.averageMinutesOfActivityPerUserPerWeek)
                            : "--"*/
                            // stored as total minutes per year for all new walkers and cyclists; shown per week
                            project.projectInfo.increaseInPhysicalActivityOfCommunity != null
                            ? formatWithSpaceThousands(project.projectInfo.increaseInPhysicalActivityOfCommunity / 52)
                            : "--"
                          }
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ width: "60px", py: 0.5, mt: 3 }}
                        >
                          {/* total for all new active people, per year of the project */
                          project.projectInfo.lifeYearsGained != null
                            ? roundToTwo(project.projectInfo.lifeYearsGained).toFixed(2)
                            : "--"}

                        </Typography>
                      </Box>
                    </Box>

                    {/* metrics Environment*/}
                    <Box sx={{ textAlign: "center", mt: 5.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ width: "60px", py: 0.75 }}
                        >
                          {formatWithSpaceThousands(roundToTwo(project.projectInfo.carTripsRemovedFromRoad))}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ width: "60px", py: 0.75 }}
                        >
                          {project.projectInfo.co2reduced.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ width: "60px", py: 0.75 }}
                        >
                          {project.projectInfo.pm25reduced.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>              

                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Box>
      </Grid>

      {/* Button */}
      <Box 
        {...(!singleProject && {
          sx: {mt: 1, pl: "310px", display: "flex"}
        })}
        {...(singleProject && {
          sx: {mt: 1}
        })}
        >
        <Button variant="outlined" onClick={handleOpenFullResults}
          {...(!singleProject && {
            sx:{ width: (selectedProjects.length * 295 + "px"), minWidth: 200}
          })}
          {...(singleProject && {
            sx: {width: "100%"}
          })}
        >
          DISPLAY FULL RESULTS
        </Button>
        
        <FullResultsDialog
          open={openFullResults}
          onClose={handleCloseFullResults}
          projects={selectedProjects}
          title="Full Results — Impact on Indicators"
        />

      </Box>
    </Box>
  );
}
